/**
 * Scheduler Worker - Main Entry Point (PWA)
 *
 * Worker task queue for:
 * - Viewing assigned tasks
 * - Starting/stopping/completing tasks
 * - Time tracking (offline-capable)
 *
 * See DESIGN-SYSTEM.md for UI standards.
 * See CLAUDE.md for offline sync requirements.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { ErrorBoundary } from './components/ErrorBoundary';
import './index.css';

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Network-first strategy with cache fallback
      networkMode: 'offlineFirst',
      // Don't retry immediately on failure
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
